import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { useClientRecordRecommendations } from "@/lib/clientRecordQueries";
import { useRecommendationCandidates, useDecideRecommendation } from "@/lib/recommendationQueries";
import { WhyThisButton } from "../../shared/WhyThisExplanation";

const STATUS_LABELS = {
  pending_coach_review: "Awaiting your review", published: "Published to client", rejected: "Rejected",
  superseded: "Superseded", no_eligible_item: "No eligible item — practice requested",
};
const STATUS_COLORS = {
  pending_coach_review: "bg-amber-100 text-amber-800", published: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700", superseded: "bg-ink/10 text-ink-soft", no_eligible_item: "bg-ink/10 text-ink-soft",
};

function CandidatesDrilldown({ workspaceId, recId }) {
  const { data, isPending } = useRecommendationCandidates(workspaceId, recId);
  if (isPending) return <p className="text-xs text-ink-soft py-2">Loading candidates…</p>;
  if (!data) return null;
  return (
    <div className="mt-3 space-y-2" data-testid={`recommendation-candidates-${recId}`}>
      {data.candidates.map((c) => (
        <div key={c.pcd_id} className={`text-xs rounded-md p-2 border ${c.is_selected ? "border-terracotta bg-terracotta/5" : "border-ink/10"}`} data-testid={`recommendation-candidate-${c.pcd_id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-ink">{c.title_snapshot}{c.is_selected && <span className="ml-2 text-terracotta">★ selected</span>}</span>
            <span className={c.eligible ? "text-ink-soft" : "text-red-600"}>{c.eligible ? `score ${c.score}` : c.rejection_reason}</span>
          </div>
          {c.eligible && <p className="text-ink-soft mt-1">{c.rules_applied.join("; ") || "no distinguishing rules fired"}</p>}
        </div>
      ))}
    </div>
  );
}

function RecommendationRow({ workspaceId, memberId, rec }) {
  const [expanded, setExpanded] = useState(false);
  const decide = useDecideRecommendation(workspaceId, memberId);

  async function handleDecision(decision) {
    try {
      await decide.mutateAsync({ recId: rec.rec_id, decision });
      toast.success(decision === "approved" ? "Recommendation published to the client" : "Recommendation rejected");
    } catch (err) {
      toast.error("Couldn't record that decision");
    }
  }

  return (
    <div className="border border-ink/10 rounded-lg p-3 bg-white/50" data-testid={`recommendation-row-${rec.rec_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-ink font-medium">{rec.layer_key}</p>
          <p className="text-xs text-ink-soft mt-0.5">{rec.rationale_summary || "No eligible Library item was found."}</p>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${STATUS_COLORS[rec.status] || "bg-ink/10 text-ink-soft"}`} data-testid={`recommendation-status-${rec.rec_id}`}>
          {STATUS_LABELS[rec.status] || rec.status}
        </span>
      </div>
      {rec.status === "published" && (
        <div className="mt-2">
          <WhyThisButton workspaceId={workspaceId} recId={rec.rec_id} isCoach />
        </div>
      )}
      <div className="flex items-center gap-3 mt-2">
        {rec.status === "pending_coach_review" && (
          <>
            <button onClick={() => handleDecision("approved")} disabled={decide.isPending} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800 disabled:opacity-50" data-testid={`recommendation-approve-${rec.rec_id}`}>
              <CheckCircle2 size={14} /> Approve
            </button>
            <button onClick={() => handleDecision("rejected")} disabled={decide.isPending} className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50" data-testid={`recommendation-reject-${rec.rec_id}`}>
              <XCircle size={14} /> Reject
            </button>
          </>
        )}
        <button onClick={() => setExpanded((e) => !e)} className="inline-flex items-center gap-1 text-xs text-ink-soft hover:text-ink" data-testid={`recommendation-toggle-candidates-${rec.rec_id}`}>
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {expanded ? "Hide" : "Show"} candidates considered
        </button>
      </div>
      {expanded && <CandidatesDrilldown workspaceId={workspaceId} recId={rec.rec_id} />}
    </div>
  );
}

/** Master v5 Prompt 23 — Client 360's Recommendations tab: every ranking
 * run for this member, newest first, with an explainability drill-down
 * (every candidate considered — eligible or not — plus the exact
 * score_components/rules_applied that produced this run's pick). */
export default function RecommendationsTab({ workspaceId, memberId }) {
  const { data, isPending, isError } = useClientRecordRecommendations(workspaceId, memberId);

  if (isPending) return <p className="text-sm text-ink-soft" data-testid="client-360-recommendations-loading">Loading recommendations…</p>;
  if (isError) return <p className="text-sm text-red-600" data-testid="client-360-recommendations-error">Couldn't load recommendations.</p>;
  if (data.items.length === 0) {
    return (
      <p className="text-sm text-ink-soft flex items-center gap-2" data-testid="client-360-recommendations-empty">
        <Sparkles size={16} /> No recommendations computed yet — they appear automatically once the member starts practising.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="client-360-recommendations-tab">
      {data.items.map((rec) => (
        <RecommendationRow key={rec.rec_id} workspaceId={workspaceId} memberId={memberId} rec={rec} />
      ))}
    </div>
  );
}
